import React, { useState, useEffect } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  useMediaQuery,
  Button,
  Box,
  TextField,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import AddIcon from "@mui/icons-material/Add";
import { Debt, Expense } from "../../lib/types";
import CreatedViaBadge from "../common/CreatedViaBadge";

interface DebtFormProps {
  onAddDebt: (debt: Omit<Debt, "id">) => void;
  onUpdateDebt: (debt: Debt) => void;
  debtToEdit?: Debt;
  isEditing: boolean;
  expenses?: Expense[];
  isDarkMode?: boolean;
  onCancel?: () => void;
}

const DebtForm: React.FC<DebtFormProps> = ({
  onAddDebt,
  onUpdateDebt,
  debtToEdit,
  isEditing,
  expenses = [],
  isDarkMode = false,
  onCancel,
}) => {
  // Calculate paydown amount based on 33% rule
  const calculatePaydownAmount = (
    balance: number,
    creditLimit: number
  ): number => {
    if (creditLimit <= 0) return 0;

    const targetBalance = creditLimit * 0.33; // 33% of credit limit
    const currentBalance = Math.abs(balance); // Work with positive numbers

    if (currentBalance <= targetBalance) {
      return 0; // Already at or below 33%
    }

    return -(currentBalance - targetBalance); // Return as negative (amount to pay)
  };

  const [formData, setFormData] = useState<Omit<Debt, "id">>({
    account: "",
    creditor: "",
    invoiceLink: "",
    type: "Other",
    balance: 0,
    payoffStrategy: "",
    creditAffecting: "",
    creditLimit: 0,
    paydownAmount: 0,
    maxMonthlyPayment: 0,
    dueDate: "",
    dateBorrowed: "",
    paymentMatch: "",
    paymentMatchSince: "",
    status: "Active",
    statementCredits: 0,
    linkedExpenseId: undefined,
    debtQuality: undefined,
    canDefault: false,
  });
  const [maxMonthlyPaymentInput, setMaxMonthlyPaymentInput] = useState("0");
  const isWide = useMediaQuery("(min-width:640px)");
  const hasMonthlyPayment =
    Math.abs(Number(formData.maxMonthlyPayment || 0)) > 0;
  const dueDateLabel = hasMonthlyPayment
    ? "Monthly Due Date"
    : "Fixed Due Date";

  // Initialize form with debt data when editing
  useEffect(() => {
    if (isEditing && debtToEdit) {
      const calculatedPaydown = calculatePaydownAmount(
        debtToEdit.balance,
        debtToEdit.creditLimit
      );
      setFormData({
        account: debtToEdit.account,
        creditor: debtToEdit.creditor || "",
        invoiceLink: debtToEdit.invoiceLink || "",
        type: debtToEdit.type || "Other",
        balance: debtToEdit.balance,
        payoffStrategy: debtToEdit.payoffStrategy,
        creditAffecting: debtToEdit.creditAffecting,
        creditLimit: debtToEdit.creditLimit,
        paydownAmount: calculatedPaydown, // Use calculated value instead of stored value
        maxMonthlyPayment: debtToEdit.maxMonthlyPayment,
        dueDate: debtToEdit.dueDate,
        dateBorrowed: debtToEdit.dateBorrowed || "",
        paymentMatch: debtToEdit.paymentMatch || "",
        paymentMatchSince: debtToEdit.paymentMatchSince || "",
        status: debtToEdit.status,
        statementCredits: debtToEdit.statementCredits,
        linkedExpenseId: debtToEdit.linkedExpenseId,
        debtQuality: debtToEdit.debtQuality,
        canDefault: Boolean(debtToEdit.canDefault),
      });
      setMaxMonthlyPaymentInput(String(debtToEdit.maxMonthlyPayment ?? 0));
    }
  }, [isEditing, debtToEdit]);

  useEffect(() => {
    console.log("[DebtForm] Due date label updated:", {
      maxMonthlyPayment: formData.maxMonthlyPayment,
      label: dueDateLabel,
    });
  }, [formData.maxMonthlyPayment, dueDateLabel]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value, type } = e.target as HTMLInputElement;

    let newFormData = { ...formData };

    // Convert numeric values
    if (type === "number") {
      (newFormData as any)[name] = parseFloat(value) || 0;
    } else {
      (newFormData as any)[name] = value;
    }

    if (name === "dateBorrowed") {
      console.log("[DebtForm] Date borrowed changed:", value);
    }

    // Auto-calculate paydown amount when balance or credit limit changes
    if (name === "balance" || name === "creditLimit") {
      const balance =
        name === "balance" ? parseFloat(value) || 0 : formData.balance;
      const creditLimit =
        name === "creditLimit" ? parseFloat(value) || 0 : formData.creditLimit;
      newFormData.paydownAmount = calculatePaydownAmount(balance, creditLimit);
    }

    setFormData(newFormData);
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    console.log("[DebtForm] Select change:", { name, value });
    setFormData({ ...formData, [name as string]: value });
  };

  const handleNumericInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    // Allow only digits, negative sign, and decimal point
    const sanitized = value.replace(/[^0-9.-]/g, "");
    // Ensure only one negative sign at the start
    const hasNegative = sanitized.startsWith("-");
    const digitsOnly = sanitized.replace(/-/g, "");
    const finalValue = hasNegative ? `-${digitsOnly}` : digitsOnly;

    // Limit to 8 characters
    const limited = finalValue.slice(0, 8);
    const numericValue = parseFloat(limited) || 0;

    let newFormData = { ...formData, [name]: numericValue };

    // Auto-calculate paydown amount when balance or credit limit changes
    if (name === "balance" || name === "creditLimit") {
      const balance = name === "balance" ? numericValue : formData.balance;
      const creditLimit =
        name === "creditLimit" ? numericValue : formData.creditLimit;
      newFormData.paydownAmount = calculatePaydownAmount(balance, creditLimit);
    }

    setFormData(newFormData);
  };

  const handleMaxMonthlyPaymentInput = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const MAX_VALUE = 1_000_000_000;
    let next = e.target.value.replace(/[^0-9,.-]/g, "");
    next = next.replace(/(?!^)-/g, "");
    const firstDot = next.indexOf(".");
    if (firstDot !== -1) {
      next = `${next.slice(0, firstDot + 1)}${next
        .slice(firstDot + 1)
        .replace(/\./g, "")}`;
    }

    const numeric = Number(next.replace(/,/g, ""));
    if (Number.isFinite(numeric) && Math.abs(numeric) > MAX_VALUE) {
      const clamped = numeric < 0 ? -MAX_VALUE : MAX_VALUE;
      setMaxMonthlyPaymentInput(
        clamped < 0 ? "-1,000,000,000" : "1,000,000,000"
      );
      setFormData((prev) => ({ ...prev, maxMonthlyPayment: clamped }));
      return;
    }

    setMaxMonthlyPaymentInput(next);
    if (next === "" || next === "-" || next === "." || next === "-.") {
      setFormData((prev) => ({ ...prev, maxMonthlyPayment: 0 }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      maxMonthlyPayment: Number.isFinite(numeric) ? numeric : 0,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Sanitize numeric fields on submission
    const sanitizedData = {
      ...formData,
      balance: Math.round(Number(formData.balance) * 100) / 100,
      creditLimit: Math.abs(
        Math.round(Number(formData.creditLimit) * 100) / 100
      ),
      paydownAmount: Math.round(Number(formData.paydownAmount) * 100) / 100,
      maxMonthlyPayment:
        Math.round(Number(formData.maxMonthlyPayment) * 100) / 100,
      statementCredits:
        Math.round(Number(formData.statementCredits) * 100) / 100,
    };

    if (isEditing && debtToEdit) {
      console.log("[DebtForm] Submitting debt update:", sanitizedData);
      onUpdateDebt({ ...sanitizedData, id: debtToEdit.id });
    } else {
      console.log("[DebtForm] Submitting new debt:", sanitizedData);
      onAddDebt(sanitizedData);
    }

    // Reset form if not editing
    if (!isEditing) {
      setFormData({
        account: "",
        creditor: "",
        invoiceLink: "",
        type: "Other",
        balance: 0,
        payoffStrategy: "",
        creditAffecting: "",
        creditLimit: 0,
        paydownAmount: 0,
        maxMonthlyPayment: 0,
        dueDate: "",
        dateBorrowed: "",
        paymentMatch: "",
        paymentMatchSince: "",
        status: "Active",
        statementCredits: 0,
        linkedExpenseId: undefined,
        debtQuality: undefined,
        canDefault: false,
      });
      setMaxMonthlyPaymentInput("0");
    }
  };

  // Define form styles for consistent UI
  const styles = {
    form: {
      display: "flex",
      flexDirection: "column" as const,
      gap: "8px",
    },
    section: {
      marginBottom: "8px",
    },
    sectionHeader: {
      fontSize: "16px",
      fontWeight: "bold" as const,
      marginBottom: "8px",
      color: isDarkMode ? "#e2e8f0" : "#2d3748",
    },
    grid: {
      display: "grid",
      gridTemplateColumns: isWide ? "repeat(2, 1fr)" : "1fr",
      gap: "12px",
    },
    inputGroup: {
      marginBottom: "8px",
    },
    label: {
      display: "block",
      marginBottom: "4px",
      fontSize: "14px",
      color: isDarkMode ? "#a0aec0" : "#4a5568",
      fontWeight: "500" as const,
    },
    required: {
      color: "#e53e3e",
      marginLeft: "4px",
    },
    input: {
      padding: "10px 12px",
      borderRadius: "6px",
      border: isDarkMode ? "1px solid #4a5568" : "1px solid #e2e8f0",
      backgroundColor: isDarkMode ? "#2d2d2d" : "#ffffff",
      color: isDarkMode ? "#e2e8f0" : "#1a202c",
      width: "100%",
      fontSize: "14px",
      transition: "border-color 0.2s ease-in-out",
      "&:focus": {
        outline: "none",
        borderColor: "#38B2AC",
        boxShadow: "0 0 0 3px rgba(56, 178, 172, 0.1)",
      },
    },
    helperText: {
      fontSize: "12px",
      color: isDarkMode ? "#718096" : "#718096",
      marginTop: "4px",
    },
    readOnlyField: {
      padding: "10px 12px",
      borderRadius: "6px",
      border: isDarkMode ? "1px solid #4a5568" : "1px solid #e2e8f0",
      backgroundColor: isDarkMode ? "#1a1a1a" : "#f7fafc",
      color: isDarkMode ? "#a0aec0" : "#718096",
      width: "100%",
      fontSize: "14px",
      display: "flex",
      alignItems: "center",
    },
    buttonGroup: {
      marginTop: "12px",
    },
    submitButton: {
      width: "100%",
      padding: "12px",
      backgroundColor: "#38B2AC",
      color: "white",
      border: "none",
      borderRadius: "6px",
      cursor: "pointer",
      fontWeight: "bold" as const,
      transition: "background-color 0.2s ease-in-out",
      "&:hover": {
        backgroundColor: "#319795",
      },
      "&:focus": {
        outline: "none",
        boxShadow: "0 0 0 3px rgba(56, 178, 172, 0.4)",
      },
    },
    cancelButton: {
      width: "100%",
      padding: "12px",
      backgroundColor: isDarkMode ? "#2d2d2d" : "#f7fafc",
      color: isDarkMode ? "#e2e8f0" : "#4a5568",
      border: isDarkMode ? "1px solid #4a5568" : "1px solid #e2e8f0",
      borderRadius: "6px",
      cursor: "pointer",
      fontWeight: "medium" as const,
      marginTop: "8px",
      "&:hover": {
        backgroundColor: isDarkMode ? "#3d3d3d" : "#edf2f7",
      },
    },
  };

  const fieldSx = {
    "& .MuiInputBase-root": {
      backgroundColor: isDarkMode ? "#2d2d2d" : "#ffffff",
      color: isDarkMode ? "#e2e8f0" : "#1a202c",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: isDarkMode ? "#4a5568" : "#e2e8f0",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: isDarkMode ? "#718096" : "#cbd5e0",
    },
    "& .MuiFormLabel-root": {
      color: isDarkMode ? "#a0aec0" : undefined,
    },
    "& .MuiFormLabel-root.Mui-focused": {
      color: "#38B2AC",
    },
    "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#38B2AC",
    },
    "& .MuiSvgIcon-root": {
      color: isDarkMode ? "#a0aec0" : undefined,
    },
  };

  return (
    <form onSubmit={handleSubmit} style={styles.form}>
      {/* Basic info */}
      <div style={styles.section}>
        <h3 style={styles.sectionHeader}>
          Basic Information
          {debtToEdit?.createdVia && (
            <span style={{ marginLeft: 8, verticalAlign: "middle" }}>
              <CreatedViaBadge createdVia={debtToEdit.createdVia} />
            </span>
          )}
        </h3>
        <div style={styles.grid}>
          <div style={styles.inputGroup}>
            <TextField
              fullWidth
              size="small"
              id="creditor"
              name="creditor"
              label="Creditor"
              value={formData.creditor || ""}
              onChange={handleChange}
              placeholder="e.g. Chase, Rodney Hill"
              sx={fieldSx}
            />
          </div>

          <div style={styles.inputGroup}>
            <TextField
              fullWidth
              size="small"
              id="account"
              name="account"
              label="Account Name"
              value={formData.account || ""}
              onChange={handleChange}
              required
              placeholder="e.g. Amazon Credit Card"
              sx={fieldSx}
            />
          </div>
        </div>

        <div style={styles.grid}>
          <div style={styles.inputGroup}>
            <TextField
              fullWidth
              size="small"
              type="url"
              id="invoiceLink"
              name="invoiceLink"
              label="Invoice Link"
              value={formData.invoiceLink || ""}
              onChange={handleChange}
              placeholder="https://..."
              sx={fieldSx}
            />
          </div>

          <div style={styles.inputGroup}>
            <FormControl fullWidth size="small">
              <InputLabel
                id="type-label"
                sx={{ color: isDarkMode ? "#a0aec0" : undefined }}
              >
                Type
              </InputLabel>
              <Select
                labelId="type-label"
                id="type"
                name="type"
                value={formData.type}
                label="Type"
                onChange={handleSelectChange}
                sx={{
                  backgroundColor: isDarkMode ? "#2d2d2d" : "#ffffff",
                  color: isDarkMode ? "#e2e8f0" : "#1a202c",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: isDarkMode ? "#4a5568" : "#e2e8f0",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: isDarkMode ? "#718096" : "#cbd5e0",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#38B2AC",
                  },
                  "& .MuiSvgIcon-root": {
                    color: isDarkMode ? "#a0aec0" : undefined,
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: isDarkMode ? "#2d2d2d" : "#ffffff",
                      color: isDarkMode ? "#e2e8f0" : "#1a202c",
                    },
                  },
                }}
              >
                <MenuItem value="Credit Card">Credit Card</MenuItem>
                <MenuItem value="Loan">Loan</MenuItem>
                <MenuItem value="Personal Loan">Personal Loan</MenuItem>
                <MenuItem value="Revolving">Revolving</MenuItem>
                <MenuItem value="Auto Loan">Auto Loan</MenuItem>
                <MenuItem value="Business Loan">Business Loan</MenuItem>
                <MenuItem value="Medical Loan">Medical Loan</MenuItem>
                <MenuItem value="Taxes">Taxes</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>
          </div>

          <div style={styles.inputGroup}>
            <FormControl fullWidth size="small">
              <InputLabel
                id="status-label"
                sx={{ color: isDarkMode ? "#a0aec0" : undefined }}
              >
                Status
              </InputLabel>
              <Select
                labelId="status-label"
                id="status"
                name="status"
                value={formData.status}
                label="Status"
                onChange={handleSelectChange}
                sx={{
                  backgroundColor: isDarkMode ? "#2d2d2d" : "#ffffff",
                  color: isDarkMode ? "#e2e8f0" : "#1a202c",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: isDarkMode ? "#4a5568" : "#e2e8f0",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: isDarkMode ? "#718096" : "#cbd5e0",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#38B2AC",
                  },
                  "& .MuiSvgIcon-root": {
                    color: isDarkMode ? "#a0aec0" : undefined,
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: isDarkMode ? "#2d2d2d" : "#ffffff",
                      color: isDarkMode ? "#e2e8f0" : "#1a202c",
                    },
                  },
                }}
              >
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Paid Off">Paid Off</MenuItem>
                <MenuItem value="In Collections">In Collections</MenuItem>
                <MenuItem value="Charged Off">Charged Off</MenuItem>
                <MenuItem value="Deferred">Deferred</MenuItem>
              </Select>
            </FormControl>
          </div>
        </div>

        <div style={styles.inputGroup}>
          <label style={styles.label}>Debt Quality</label>
          <div
            style={{ display: "flex", gap: "12px", flexWrap: "wrap" as const }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                cursor: "pointer",
                padding: "6px 12px",
                borderRadius: "6px",
                border:
                  formData.debtQuality === "good"
                    ? "2px solid #38A169"
                    : isDarkMode
                      ? "1px solid #4a5568"
                      : "1px solid #e2e8f0",
                backgroundColor:
                  formData.debtQuality === "good"
                    ? isDarkMode
                      ? "#1a3a2a"
                      : "#F0FFF4"
                    : "transparent",
              }}
            >
              <input
                type="radio"
                name="debtQuality"
                value="good"
                checked={formData.debtQuality === "good"}
                onChange={handleChange}
                style={{ cursor: "pointer" }}
              />
              <span
                style={{
                  color:
                    formData.debtQuality === "good"
                      ? "#38A169"
                      : isDarkMode
                        ? "#a0aec0"
                        : "#4a5568",
                }}
              >
                Good Debt
              </span>
            </label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                cursor: "pointer",
                padding: "6px 12px",
                borderRadius: "6px",
                border:
                  formData.debtQuality === "bad"
                    ? "2px solid #E53E3E"
                    : isDarkMode
                      ? "1px solid #4a5568"
                      : "1px solid #e2e8f0",
                backgroundColor:
                  formData.debtQuality === "bad"
                    ? isDarkMode
                      ? "#3a1a1a"
                      : "#FFF5F5"
                    : "transparent",
              }}
            >
              <input
                type="radio"
                name="debtQuality"
                value="bad"
                checked={formData.debtQuality === "bad"}
                onChange={handleChange}
                style={{ cursor: "pointer" }}
              />
              <span
                style={{
                  color:
                    formData.debtQuality === "bad"
                      ? "#E53E3E"
                      : isDarkMode
                        ? "#a0aec0"
                        : "#4a5568",
                }}
              >
                Bad Debt
              </span>
            </label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                cursor: "pointer",
                padding: "6px 12px",
                borderRadius: "6px",
                border: !formData.debtQuality
                  ? "2px solid #718096"
                  : isDarkMode
                    ? "1px solid #4a5568"
                    : "1px solid #e2e8f0",
                backgroundColor: !formData.debtQuality
                  ? isDarkMode
                    ? "#2d2d2d"
                    : "#F7FAFC"
                  : "transparent",
              }}
            >
              <input
                type="radio"
                name="debtQuality"
                value=""
                checked={!formData.debtQuality}
                onChange={(e) => {
                  setFormData({ ...formData, debtQuality: undefined });
                }}
                style={{ cursor: "pointer" }}
              />
              <span
                style={{
                  color: !formData.debtQuality
                    ? "#718096"
                    : isDarkMode
                      ? "#a0aec0"
                      : "#4a5568",
                }}
              >
                Not Set
              </span>
            </label>
          </div>
          <div style={styles.helperText}>
            Good debt builds assets (RV, Auto, Mortgage). Bad debt is for
            consumables (Credit Cards).
          </div>
        </div>

        <div style={styles.inputGroup}>
          <FormControl fullWidth size="small">
            <InputLabel
              id="can-default-label"
              sx={{ color: isDarkMode ? "#a0aec0" : undefined }}
            >
              Can Default
            </InputLabel>
            <Select
              labelId="can-default-label"
              id="canDefault"
              name="canDefault"
              value={formData.canDefault ? "yes" : "no"}
              label="Can Default"
              onChange={(e) =>
                setFormData({
                  ...formData,
                  canDefault: String(e.target.value) === "yes",
                })
              }
              sx={{
                backgroundColor: isDarkMode ? "#2d2d2d" : "#ffffff",
                color: isDarkMode ? "#e2e8f0" : "#1a202c",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: isDarkMode ? "#4a5568" : "#e2e8f0",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: isDarkMode ? "#718096" : "#cbd5e0",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#38B2AC",
                },
                "& .MuiSvgIcon-root": {
                  color: isDarkMode ? "#a0aec0" : undefined,
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: isDarkMode ? "#2d2d2d" : "#ffffff",
                    color: isDarkMode ? "#e2e8f0" : "#1a202c",
                  },
                },
              }}
            >
              <MenuItem value="no">No</MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
            </Select>
          </FormControl>
          <div style={styles.helperText}>
            Marks debts that could be strategically defaulted.
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "12px",
            alignItems: "start",
          }}
        >
          <div style={styles.inputGroup}>
            <TextField
              fullWidth
              size="small"
              type="text"
              inputMode="numeric"
              id="balance"
              name="balance"
              label="Balance ($)"
              value={formData.balance === 0 ? "" : String(formData.balance)}
              onChange={handleNumericInput}
              inputProps={{ maxLength: 8, inputMode: "numeric" }}
              required
              placeholder="e.g. -1000"
              sx={fieldSx}
            />
          </div>

          <div style={styles.inputGroup}>
            <TextField
              fullWidth
              size="small"
              type="text"
              inputMode="numeric"
              id="creditLimit"
              name="creditLimit"
              label="Credit Limit ($)"
              value={
                formData.creditLimit === 0 ? "" : String(formData.creditLimit)
              }
              onChange={handleNumericInput}
              inputProps={{ maxLength: 8, inputMode: "numeric" }}
              placeholder="e.g. 5000"
              sx={fieldSx}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Paydown ($)</label>
            <div style={styles.readOnlyField}>
              ${Math.abs(Number(formData.paydownAmount || 0)).toFixed(2)}
            </div>
          </div>
        </div>
        <div style={styles.helperText}>
          Balance: use negative for debts. Paydown target: 33% of credit limit.
        </div>
      </div>

      {/* Credit information */}
      <div style={styles.section}>
        <h3 style={styles.sectionHeader}>Credit Information</h3>
        <div style={styles.grid}>
          <div style={styles.inputGroup}>
            <TextField
              fullWidth
              size="small"
              id="payoffStrategy"
              name="payoffStrategy"
              label="Payoff Strategy"
              value={formData.payoffStrategy || ""}
              onChange={handleChange}
              placeholder="e.g. Tiny House"
              sx={{
                ...fieldSx,
              }}
            />
          </div>

          <div style={styles.inputGroup}>
            <FormControl fullWidth size="small">
              <InputLabel
                id="creditAffecting-label"
                sx={{ color: isDarkMode ? "#a0aec0" : undefined }}
              >
                Credit Affecting
              </InputLabel>
              <Select
                labelId="creditAffecting-label"
                id="creditAffecting"
                name="creditAffecting"
                value={formData.creditAffecting || ""}
                label="Credit Affecting"
                onChange={handleSelectChange}
                sx={{
                  backgroundColor: isDarkMode ? "#2d2d2d" : "#ffffff",
                  color: isDarkMode ? "#e2e8f0" : "#1a202c",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: isDarkMode ? "#4a5568" : "#e2e8f0",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: isDarkMode ? "#718096" : "#cbd5e0",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#38B2AC",
                  },
                  "& .MuiSvgIcon-root": {
                    color: isDarkMode ? "#a0aec0" : undefined,
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: isDarkMode ? "#2d2d2d" : "#ffffff",
                      color: isDarkMode ? "#e2e8f0" : "#1a202c",
                    },
                  },
                }}
              >
                <MenuItem value="">Select...</MenuItem>
                <MenuItem value="Spencer">Spencer</MenuItem>
                <MenuItem value="Brittany">Brittany</MenuItem>
                <MenuItem value="Spencer & Brittany">
                  Spencer & Brittany
                </MenuItem>
              </Select>
            </FormControl>
          </div>
        </div>

        <div style={styles.inputGroup}>
          <label style={styles.label}>Available Credit ($)</label>
          <div style={styles.readOnlyField}>
            $
            {(
              Number(formData.creditLimit || 0) + Number(formData.balance || 0)
            ).toFixed(2)}{" "}
            (Auto-calculated)
          </div>
        </div>

        <div style={styles.inputGroup}>
          <label style={styles.label}>Credit Card Utilization</label>
          <div style={styles.readOnlyField}>
            {Number(formData.creditLimit || 0) > 0
              ? `${Math.round(
                  (Math.abs(Number(formData.balance || 0)) /
                    Number(formData.creditLimit || 1)) *
                    100
                )}% (Auto-calculated)`
              : "0% (Auto-calculated)"}
          </div>
        </div>
      </div>

      {/* Payment details */}
      <div style={styles.section}>
        <h3 style={styles.sectionHeader}>Payment Details</h3>

        <div style={styles.inputGroup}>
          <FormControl fullWidth size="small">
            <InputLabel
              id="linkedExpenseId-label"
              sx={{ color: isDarkMode ? "#a0aec0" : undefined }}
            >
              Associate with Expense
            </InputLabel>
            <Select
              labelId="linkedExpenseId-label"
              id="linkedExpenseId"
              name="linkedExpenseId"
              value={formData.linkedExpenseId || ""}
              label="Associate with Expense"
              onChange={handleSelectChange}
              sx={{
                backgroundColor: isDarkMode ? "#2d2d2d" : "#ffffff",
                color: isDarkMode ? "#e2e8f0" : "#1a202c",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: isDarkMode ? "#4a5568" : "#e2e8f0",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: isDarkMode ? "#718096" : "#cbd5e0",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#38B2AC",
                },
                "& .MuiSvgIcon-root": {
                  color: isDarkMode ? "#a0aec0" : undefined,
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: isDarkMode ? "#2d2d2d" : "#ffffff",
                    color: isDarkMode ? "#e2e8f0" : "#1a202c",
                  },
                },
              }}
            >
              <MenuItem value="">No associated expense</MenuItem>
              {expenses
                .filter(
                  (expense) => !expense.isPaid && !expense.isRecurringInstance
                )
                .map((expense) => (
                  <MenuItem key={expense.id} value={expense.id}>
                    {expense.name} - {expense.vendor || "Unknown Vendor"} ($
                    {Math.abs(Number(expense.amount || 0)).toFixed(2)})
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <div style={styles.helperText}>
            {formData.linkedExpenseId
              ? "This debt will sync its monthly payment with the associated expense"
              : "Select an expense to automatically sync monthly payments"}
          </div>
        </div>

        <div style={styles.inputGroup}>
          <TextField
            fullWidth
            size="small"
            type="text"
            inputMode="decimal"
            id="maxMonthlyPayment"
            name="maxMonthlyPayment"
            label="Max Monthly Payment ($)"
            value={maxMonthlyPaymentInput}
            onChange={handleMaxMonthlyPaymentInput}
            placeholder="e.g. -100"
            inputProps={{ inputMode: "decimal" }}
            sx={fieldSx}
          />
          <div style={styles.helperText}>
            Allowed: digits, "-", ",", "." up to ±$1,000,000,000.
          </div>
        </div>

        <div style={styles.grid}>
          <div style={styles.inputGroup}>
            <TextField
              fullWidth
              size="small"
              type="text"
              id="dueDate"
              name="dueDate"
              label={dueDateLabel}
              value={formData.dueDate || ""}
              onChange={handleChange}
              placeholder="e.g. 15th"
              sx={fieldSx}
            />
          </div>

          <div style={styles.inputGroup}>
            <TextField
              fullWidth
              size="small"
              type="date"
              id="dateBorrowed"
              name="dateBorrowed"
              label="Date Borrowed"
              value={formData.dateBorrowed || ""}
              onChange={handleChange}
              sx={fieldSx}
              InputLabelProps={{ shrink: true }}
            />
          </div>

          <div style={styles.inputGroup}>
            <TextField
              fullWidth
              size="small"
              id="paymentMatch"
              name="paymentMatch"
              label="Auto-match payments from linked accounts"
              value={formData.paymentMatch || ""}
              onChange={handleChange}
              placeholder="e.g. Matt Needham"
              helperText="Outgoing transactions whose name contains this text are recorded as payments and lower the balance."
              sx={fieldSx}
            />
          </div>

          <div style={styles.inputGroup}>
            <TextField
              fullWidth
              size="small"
              type="date"
              id="paymentMatchSince"
              name="paymentMatchSince"
              label="Match payments since"
              value={formData.paymentMatchSince || ""}
              onChange={handleChange}
              sx={fieldSx}
              InputLabelProps={{ shrink: true }}
            />
          </div>

          <div style={styles.inputGroup}>
            <TextField
              fullWidth
              size="small"
              type="number"
              id="statementCredits"
              name="statementCredits"
              label="Statement Credits ($)"
              value={formData.statementCredits ?? ""}
              onChange={handleChange}
              placeholder="e.g. 0"
              inputProps={{ step: "0.01" }}
              sx={fieldSx}
            />
          </div>
        </div>
      </div>

      {/* Form actions */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mt: 2,
          flexDirection: isEditing ? "row" : "column",
        }}
      >
        <Button
          type="submit"
          variant="contained"
          startIcon={isEditing ? <SaveIcon /> : <AddIcon />}
          sx={{
            flex: isEditing ? 1 : undefined,
            backgroundColor: "#38B2AC",
            "&:hover": { backgroundColor: "#319795" },
          }}
        >
          {isEditing ? "Update" : "Add Debt"}
        </Button>
        {isEditing && onCancel && (
          <Button
            type="button"
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={onCancel}
            sx={{
              flex: 1,
              borderColor: isDarkMode ? "#4a5568" : "#e2e8f0",
              color: isDarkMode ? "#e2e8f0" : "#4a5568",
              "&:hover": {
                borderColor: isDarkMode ? "#718096" : "#cbd5e0",
                backgroundColor: isDarkMode
                  ? "rgba(255,255,255,0.05)"
                  : "rgba(0,0,0,0.02)",
              },
            }}
          >
            Cancel
          </Button>
        )}
      </Box>
    </form>
  );
};

export default DebtForm;
